import React, {FC, useCallback, useEffect, useState} from 'react'
import {Form, Formik, FormikProps} from 'formik'
import {ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

import {
  ICashCollectionModel,
  initialCashCollection,
  cashCollectionValidation,
} from './CashCollectionModel'

import {
  createCashCollection,
  deleteCashCollection,
  getAllCashCollections,
  updateCashCollection,
} from './Api'

import CashCollectionTable, { CashCollectionRow } from './CashCollectionTable'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import FormTabHeader, {TabOption} from '../../../../shared/components/FormTabHeader'
import TextInput from '../../../../shared/components/TextInput'
import SelectInput from '../../../../shared/components/SelectInput'
import {branches, currencies} from '../../../../shared/models/SharedApiLookups'



const CashCollectionPage: FC = () => {
  const [tab, setTab] = useState<string | 'form'>('form')
  const [cashCollections, setCashCollections] = useState<ICashCollectionModel[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null | undefined>(null)
  const [showModal, setShowModal] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [sortBy, setSortBy] = useState<string>('createdDate')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [error, setError] = useState<string | null>(null)
  const [branchOptions, setBranchOptions] = useState<{value: string; label: string}[]>([])
  const [currencyOptions, setCurrencyOptions] = useState<{value: string; label: string}[]>([])

  const tabOptions: TabOption<'form' | 'list'>[] = [
    {key: 'form', title: 'Yeni Tahsilat Kaydı', icon: 'bi bi-cash'},
    {key: 'list', title: 'Tahsilat Listesi', icon: 'bi bi-list'},
  ]

  useEffect(() => {
    Promise.all([branches(), currencies()])
      .then(([br, cr]) => {
        setBranchOptions(br)
        setCurrencyOptions(cr)
      })
      .catch(() => void 0)
  }, [])

  const fetchCashCollections = useCallback(
    async (page: number = 0, sort?: string, order?: 'asc' | 'desc') => {
      setLoading(true)
      setError(null)
      try {
        const res = await getAllCashCollections(page, 10, sort ?? sortBy, order ?? sortOrder)
        const {hasError, payload, message} = res.data
        if (hasError) throw new Error(message || 'Failed to fetch cash collections')
        setCashCollections(payload.content)
        setTotalPages(payload.totalPages)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error')
        showErrorToast(error || 'Liste alınamadı')
      } finally {
        setLoading(false)
      }
    },
    [sortBy, sortOrder, error]
  )

  useEffect(() => {
    if (tab === 'list') fetchCashCollections(0)
  }, [tab, fetchCashCollections])

  const handleSave = async (values: ICashCollectionModel, resetForm: () => void) => {
    setLoading(true)
    try {
      if (selectedId) {
        await updateCashCollection({...values, id: selectedId})
        showSuccessToast('Tahsilat başarıyla güncellendi')
      } else {
        await createCashCollection(values)
        showSuccessToast('Tahsilat başarıyla oluşturuldu')
      }
      setCurrentPage(0)
      await fetchCashCollections(0, undefined, undefined)
      resetForm()
      setSelectedId(null)
      setTab('list')
    } catch {
      showErrorToast('Bir hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = useCallback(
    (row: ICashCollectionModel, formik: FormikProps<ICashCollectionModel>) => {
      formik.setValues(row)
      setSelectedId(row.id != null ? String(row.id) : null) 
      setTab('form')
    },
    []
  )

  const handleDeleteClick = useCallback((row: ICashCollectionModel) => {
    setSelectedId(row.id != null ? String(row.id) : null)
    setShowModal(true)
  }, [])

  const handleDelete = async () => {
    if (selectedId) {
      setLoading(true)
      try {
        await deleteCashCollection(selectedId)
        showSuccessToast('Tahsilat başarıyla silindi')
        await fetchCashCollections(currentPage)
      } catch (err) {
        console.error(err)
        showErrorToast('Silinirken bir hata oluştu')
      } finally {
        setLoading(false)
        setShowModal(false)
        setSelectedId(null)
      }
    }
  }

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
        fetchCashCollections(newPage, sortBy, sortOrder)
      }
    },
    [totalPages, sortBy, sortOrder, fetchCashCollections]
  )

  const handleSort = useCallback(
    (column: string, order: 'asc' | 'desc') => {
      setSortBy(column)
      setSortOrder(order)
      fetchCashCollections(currentPage, column, order)
    },
    [currentPage, fetchCashCollections]
  )

const toDate = (v?: string | Date) => {
  if (!v) return undefined
  return v instanceof Date ? v : new Date(v)
}


const toTableRows = (list: ICashCollectionModel[]): CashCollectionRow[] =>
  list.map((r) => ({
    ...r,
    id: r.id != null ? String(r.id) : undefined,
    createdAt: toDate(r.createdAt),
    updatedAt: toDate(r.updatedAt),
    transactionDate: toDate(r.transactionDate),
  }))


  return (
    <>
      <Formik
        initialValues={initialCashCollection}
        validationSchema={cashCollectionValidation}
        enableReinitialize={true}
        onSubmit={(values, {resetForm}) => handleSave(values, resetForm)}
      >
        {(formik) => {
          const handleReset = () => {
            formik.resetForm()
            setSelectedId(null)
          }

          return (
            <>
              <FormTabHeader
                formId='cashCollectionForm'
                formikSubmit={formik.handleSubmit}
                formikReset={handleReset}
                loading={loading}
                tab={tab}
                setTab={setTab}
                setSelectedId={setSelectedId}
                tabs={tabOptions}
                onExportExcel={() => console.log('Excel export')}
              />

              {tab === 'form' && (
                <div className='card mb-5'>
                  <Form id='cashCollectionForm' className='form card-body'>
                    <div className='row mb-6'>
                      <TextInput name='searchText' label='Cari Ara' wrapperClassName='col-md-6' />
                      <TextInput
                        name='receiptNumber'
                        label='Makbuz Numarası'
                        wrapperClassName='col-md-6'
                      />
                    </div>
                    <div className='row mb-6'>
                      <TextInput name='firstName' label='Cari Ad' wrapperClassName='col-md-6' />
                      <TextInput
                        name='lastName'
                        label='Devamı / Soyad'
                        wrapperClassName='col-md-6'
                      />
                    </div>
                    <div className='row mb-6'>
                      <TextInput
                        name='transactionDate'
                        type='date'
                        label='Tarih'
                        wrapperClassName='col-md-6'
                      />
                      <TextInput
                        name='dueDate'
                        type='date'
                        label='Vade'
                        wrapperClassName='col-md-6'
                      />
                    </div>

                    <div className='row mb-6'>
                      <TextInput
                        name='cashAmount'
                        type='number'
                        label='Tutar'
                        wrapperClassName='col-md-6'
                      />
                      <SelectInput
                        name='cashAmountCurrency'
                        label='Para Birimi'
                        wrapperClassName='col-md-6'
                        options={currencyOptions}
                      />
                    </div>

                    <div className='row mb-6'>
                      <TextInput
                        name='cashboxRate'
                        type='number'
                        label='Kur'
                        wrapperClassName='col-md-6'
                      />
                      <SelectInput
                        name='branch'
                        label='İşlem Şube'
                        wrapperClassName='col-md-6'
                        options={branchOptions}
                      />
                    </div>
                    <div className='row mb-6'>
                      <TextInput name='operator' label='İşlemi Yapan' wrapperClassName='col-md-6' />
                      <TextInput name='description' label='Açıklama' wrapperClassName='col-md-6' />
                    </div>
                  </Form>
                </div>
              )}

              {tab === 'list' && (
                <div className='card'>
                  <div className='card-body pt-0'>
                    <CashCollectionTable
                      data={toTableRows(cashCollections)}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      onEdit={(row) => handleEdit(row as unknown as ICashCollectionModel, formik)}
                      onDelete={(row) => handleDeleteClick(row as unknown as ICashCollectionModel)}
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                      loading={loading}
                    />
                  </div>
                </div>
              )}
            </>
          )
        }}
      </Formik>

      <ConfirmModal
        show={showModal}
        header='Silme Onayı'
        text='Bu kaydı silmek istediğinize emin misiniz?'
        handleClose={() => {
          setShowModal(false)
          setSelectedId(null)
        }}
        applyOnClick={handleDelete}
      />

      <ToastContainer position='top-right' autoClose={5000} newestOnTop pauseOnHover />
    </>
  )
}

export default CashCollectionPage
